/**
 * EwayBillScreen — generate the e-Way Bill bulk-import JSON for high-value
 * consignments (> ₹50,000). Lists eligible sale invoices, collects the
 * transport leg per invoice, validates, and exports the portal JSON.
 *
 * Transport details are entered here (not stored in the DB in v1). Plan-gated
 * behind Data Export (PRO); permission-gated behind reports:gst.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileCode, RefreshCw, Truck } from 'lucide-react';
import { useAppStore } from '@/store/useAppStore';
import { usePlanGate, FEATURE_DATA_EXPORT } from '@/plans';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { startOfDay, toDateInputValue, fromDateInputValue } from '@/utils/dateRange';
import { Button } from '@/components/ui/Button';
import { file } from '@/platform';
import { loadEwayCandidates, type EwbCandidate } from '@/reports/eway/ewayBillDataSource';
import { buildEwayBillJson, validateEwb, type EwbTransport, type TransMode } from '@/reports/eway/ewayBill';

type Preset = 'month' | 'lastMonth' | 'custom';

function endOfDay(d: Date): Date { const c = new Date(d); c.setHours(23, 59, 59, 999); return c; }

function presetRange(preset: Preset, custom: [Date, Date]): [Date, Date] {
  const now = new Date();
  if (preset === 'month') return [startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), endOfDay(now)];
  if (preset === 'lastMonth') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return [startOfDay(from), endOfDay(to)];
  }
  return custom;
}

const MODE_LABEL: Record<TransMode, string> = { '1': 'Road', '2': 'Rail', '3': 'Air', '4': 'Ship' };

function emptyTransport(): EwbTransport {
  return { mode: '1', distanceKm: 0, vehicleNo: '', transporterId: '', transporterName: '' };
}

export function EwayBillScreen() {
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const { canAccess } = usePlanGate();
  const [preset, setPreset] = useState<Preset>('month');
  const [customFrom, setCustomFrom] = useState(toDateInputValue(startOfDay(new Date(new Date().getFullYear(), new Date().getMonth(), 1))));
  const [customTo, setCustomTo] = useState(toDateInputValue(new Date()));
  const [candidates, setCandidates] = useState<EwbCandidate[]>([]);
  const [transport, setTransport] = useState<Record<string, EwbTransport>>({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const range = useCallback((): [Date, Date] => {
    return presetRange(preset, [fromDateInputValue(customFrom), endOfDay(fromDateInputValue(customTo))]);
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    if (!activeStoreId) return;
    setLoading(true);
    try {
      const [from, to] = range();
      const rows = await loadEwayCandidates(activeStoreId, from, to);
      setCandidates(rows);
      setTransport((prev) => {
        const next: Record<string, EwbTransport> = {};
        for (const c of rows) next[c.invoiceId] = prev[c.invoiceId] ?? emptyTransport();
        return next;
      });
    } finally {
      setLoading(false);
    }
  }, [activeStoreId, range]);

  useEffect(() => { void load(); }, [load]);

  const patch = (id: string, p: Partial<EwbTransport>) =>
    setTransport((prev) => ({ ...prev, [id]: { ...prev[id], ...p } }));

  // Validate each candidate against its current transport entry.
  const validity = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const c of candidates) {
      map.set(c.invoiceId, validateEwb({ ...c.base, transport: transport[c.invoiceId] ?? emptyTransport() }));
    }
    return map;
  }, [candidates, transport]);

  const readyIds = candidates.filter((c) => (validity.get(c.invoiceId) ?? []).length === 0).map((c) => c.invoiceId);

  const handleExport = async () => {
    if (readyIds.length === 0) return;
    setExporting(true);
    try {
      const ready = candidates
        .filter((c) => readyIds.includes(c.invoiceId))
        .map((c) => ({ ...c.base, transport: transport[c.invoiceId] }));
      const [from] = range();
      await file.saveText({
        content: JSON.stringify(buildEwayBillJson(ready), null, 2),
        defaultFileName: `EwayBill_${toDateInputValue(from)}.json`,
        filters: [{ name: 'E-Way Bill JSON', extensions: ['json'] }],
      });
    } finally {
      setExporting(false);
    }
  };

  if (!canAccess(FEATURE_DATA_EXPORT)) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-heading text-fg-primary">E-Way Bill requires the Pro plan</div>
          <div className="mt-1 text-body text-fg-tertiary">Upgrade to Professional or Enterprise to generate e-way bills.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4 overflow-auto pb-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        {(['month', 'lastMonth', 'custom'] as Preset[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPreset(p)}
            className={cn(
              'rounded-md px-3 py-1.5 text-body transition-colors',
              preset === p ? 'bg-accent/10 text-accent-ink' : 'text-fg-secondary hover:bg-bg-subtle',
            )}
          >
            {p === 'month' ? 'This month' : p === 'lastMonth' ? 'Last month' : 'Custom'}
          </button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2">
            <input type="date" value={customFrom} max={customTo} title="From date" aria-label="From date"
              onChange={(e) => setCustomFrom(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
            <span className="text-fg-tertiary">–</span>
            <input type="date" value={customTo} min={customFrom} title="To date" aria-label="To date"
              onChange={(e) => setCustomTo(e.target.value)}
              className="rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
          </Button>
          <Button variant="primary" onClick={() => void handleExport()} disabled={readyIds.length === 0 || exporting}>
            <FileCode size={14} className="mr-1.5" /> {exporting ? 'Exporting…' : `Export EWB JSON (${readyIds.length})`}
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-label text-fg-tertiary">
        <Truck size={14} />
        <span>{candidates.length} invoice{candidates.length === 1 ? '' : 's'} over ₹50,000 in this period. Fill transport details to enable export.</span>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center text-body text-fg-tertiary">Loading…</div>
      ) : candidates.length === 0 ? (
        <div className="rounded-lg border border-stroke-hairline/10 bg-bg-surface px-4 py-10 text-center text-body text-fg-tertiary">
          No consignments above the ₹50,000 e-way bill threshold in this period.
        </div>
      ) : (
        <div className="space-y-3">
          {candidates.map((c) => {
            const t = transport[c.invoiceId] ?? emptyTransport();
            const errs = validity.get(c.invoiceId) ?? [];
            const ready = errs.length === 0;
            return (
              <div key={c.invoiceId} className={cn('rounded-lg border bg-bg-surface px-4 py-3', ready ? 'border-success/30' : 'border-stroke-hairline/10')}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    {ready ? <CheckCircle2 size={16} className="text-success-ink" /> : <AlertTriangle size={16} className="text-warning-ink" />}
                    <span className="font-mono text-body text-fg-primary">{c.invoiceNumber}</span>
                    <span className="text-label text-fg-tertiary">· {c.customerName}</span>
                  </div>
                  <span className="text-body font-medium tabular-nums text-fg-primary">{formatCurrency(c.value)}</span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                  <Field label="Mode">
                    <select
                      value={t.mode}
                      onChange={(e) => patch(c.invoiceId, { mode: e.target.value as TransMode })}
                      className="w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary"
                    >
                      {(Object.keys(MODE_LABEL) as TransMode[]).map((m) => (
                        <option key={m} value={m}>{MODE_LABEL[m]}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Distance (km)">
                    <input type="number" min={0} value={t.distanceKm || ''}
                      onChange={(e) => patch(c.invoiceId, { distanceKm: Number(e.target.value) })}
                      className="w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
                  </Field>
                  <Field label="Vehicle no.">
                    <input type="text" value={t.vehicleNo ?? ''} placeholder="KA01AB1234"
                      onChange={(e) => patch(c.invoiceId, { vehicleNo: e.target.value })}
                      className="w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
                  </Field>
                  <Field label="Transporter ID">
                    <input type="text" value={t.transporterId ?? ''} placeholder="GSTIN / TRANSIN"
                      onChange={(e) => patch(c.invoiceId, { transporterId: e.target.value })}
                      className="w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
                  </Field>
                  <Field label="Transporter">
                    <input type="text" value={t.transporterName ?? ''}
                      onChange={(e) => patch(c.invoiceId, { transporterName: e.target.value })}
                      className="w-full rounded-md border border-stroke-hairline/15 bg-bg-surface px-2 py-1 text-body text-fg-primary" />
                  </Field>
                </div>

                {errs.length > 0 && (
                  <div className="mt-2 text-label text-warning-ink">{errs.join(' · ')}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-label uppercase text-fg-tertiary">{label}</span>
      {children}
    </label>
  );
}
